/*======================================================
 VORYNEX Blueprint V2
 State Engine
 Version 2.0
======================================================*/

(function () {

    "use strict";

    console.log("Loading State Engine...");

    //--------------------------------------------------
    // Namespace
    //--------------------------------------------------

    window.VORYNEX = window.VORYNEX || {};

    //--------------------------------------------------
    // Private State
    //--------------------------------------------------

    const state = {

        profile: {

            gender: "male",

            age: 27,

            height: 177,

            weight: 82

        },

        goal: {

            goal: "cut",

            activity: "moderate",

            workouts: 5

        },

        nutrition: {

            calories: 0,

            protein: 0,

            carbs: 0,

            fats: 0

        },

        mealPlan: [],

        supplements: [],

        scores: {},

        ui: {},

        // Stores the full computed result from vpx-engine.js
        // (calories, macros, scores, goal projection, etc.)
        // Shape defined and consumed by vpx-render.js getData().
        blueprint: null,

        // Tracks onboarding wizard completion + raw answers,
        // used by vpx-render.js to decide whether to show the
        // wizard or the dashboard.
        onboarding: {

            completed: false,

            answers: null

        },

        // Recurring weekly training-day pattern: { days: [1,3,5] }
        // where day numbers follow JS Date.getDay() (0=Sun..6=Sat).
        trainingSchedule: {

            days: []

        },

        // Free-text personal notes keyed by date string "YYYY-MM-DD".
        dailyNotes: {},

        // Lightweight email lead-capture (Dashboard banner + Reports
        // gate share this same section). No backend exists, so
        // "subscribed" just means the request was sent to Shopify's
        // native customer/newsletter endpoint — see vpx-render.js.
        leadCapture: {

            email: null,

            subscribed: false,

            dismissed: false

        },

        // Per-exercise strength/rep progress log, entered manually by
        // the person after a session. Keyed by exercise id (e.g.
        // "squat"); each value is a chronological array of
        // { date: "YYYY-MM-DD", weight: number, reps: number }.
        // Powers the "Πρόοδος" panel on the Training Program page and
        // the Dashboard consistency streak.
        exerciseLogs: {},

        // Weekly bodyweight check-ins, entered manually by the person.
        // Array of { date: "YYYY-MM-DD", weightKg: number }, chronological.
        // Compared against the engine's predicted weekly rate (data.goal
        // .weeklyRateKg) to power the "adaptive recalibration" suggestion —
        // same honest-feedback spirit as the Adaptive Timeline, but using
        // REAL observed data instead of a one-time projection.
        weightCheckIns: [],

        // Display preferences. Weights are always STORED in kg
        // internally (single source of truth, avoids conversion bugs
        // compounding over repeated saves) — "units" only controls how
        // already-stored values are FORMATTED for display.
        settings: {
            units: "metric" // "metric" (kg) | "imperial" (lb)
        },

        // Avatar customization — fully custom, no default face/body to
        // avoid depicting anyone specific. bodyStage is NOT set here;
        // it's computed live by vpx-avatar.js from goal progress, never
        // stored (so it always reflects current data, not a stale save).
        avatarConfig: {
            gender: "male",              // "male" | "female"
            skinTone: "tone3",           // tone1..tone5, light to dark
            hairStyle: "short",
            hairColor: "#2b2118",
            outfitType: "tshirt",        // "tshirt" | "tank" | "hoodie" | "tracksuit" | "leggings"
            outfitColor: "#c9a227",
            accessory: "none",           // "none" | "glasses" | "cap" | "headphones" | "gymbag"
            showBranding: false,         // optional VORYNEX chest logo on outfit
            holdingShaker: false         // optional VORYNEX-branded shaker prop
        },

        // Achievement unlocks — { [achievementId]: "YYYY-MM-DD" } for
        // unlocked badges. firstSeenAt anchors the "membership length"
        // category for accounts that existed before this feature shipped
        // (no earlier signup timestamp exists anywhere in state).
        achievements: {
            unlocked: {},
            firstSeenAt: null
        }

    };

    //--------------------------------------------------
    // Local Persistence (blueprint + onboarding only)
    //--------------------------------------------------
    // Keeps the user's completed quiz + calculated
    // blueprint across page reloads / navigation in
    // THIS browser. Not the full "weekly tracking"
    // backend — just enough so leaving the page (e.g.
    // clicking a product link) never throws the user
    // back into the onboarding wizard.
    //--------------------------------------------------

    const PERSIST_KEY = "vpx_blueprint_v2";
    const PERSISTED_SECTIONS = ["blueprint", "onboarding", "trainingSchedule", "dailyNotes", "leadCapture", "exerciseLogs", "weightCheckIns", "settings", "avatarConfig", "achievements"];

    function loadPersisted() {
        try {
            const raw = localStorage.getItem(PERSIST_KEY);
            if (!raw) return;
            const saved = JSON.parse(raw);
            PERSISTED_SECTIONS.forEach(function (section) {
                if (saved && saved[section] !== undefined) {
                    state[section] = saved[section];
                }
            });
        } catch (e) {
            console.warn("VORYNEX state: could not load persisted data.", e);
        }
    }

    /*
    ==========================================
    Cloud Sync (Cloudflare Worker)
    ------------------------------------------
    Only active when the shopper is logged into
    their Shopify customer account — window
    .VPX_CUSTOMER is injected by the .liquid
    file from Shopify's own `customer` object,
    null when logged out. This is ADDITIVE, not
    a replacement: localStorage keeps working
    exactly as before for everyone, logged in or
    not, so nothing breaks for people who haven't
    registered. Logged-in customers additionally
    get their data synced to a small Cloudflare
    Worker + KV store, keyed by their Shopify
    customer ID — so the same Blueprint, exercise
    logs, and check-ins follow them to any device
    they log into.
    Save calls are debounced (800ms) so rapid
    successive changes (e.g. clicking through
    several exercise saves) don't spam the API.
    ==========================================
    */

    const VORYNEX_API_BASE = "https://vorynex-api.vorynex2026.workers.dev";
    window.VORYNEX.config.apiBase = VORYNEX_API_BASE;

    function getCustomerId() {
        return (window.VPX_CUSTOMER && window.VPX_CUSTOMER.id) ? String(window.VPX_CUSTOMER.id) : null;
    }

    function getCustomerSignature() {
        return (window.VPX_CUSTOMER && window.VPX_CUSTOMER.signature) ? window.VPX_CUSTOMER.signature : null;
    }

    let cloudSaveTimer = null;

    function scheduleCloudSave() {
        const customerId = getCustomerId();
        const signature = getCustomerSignature();
        if (!customerId || !signature) return;

        clearTimeout(cloudSaveTimer);
        cloudSaveTimer = setTimeout(function () {
            const toSave = {};
            PERSISTED_SECTIONS.forEach(function (section) {
                toSave[section] = state[section];
            });

            fetch(VORYNEX_API_BASE + "/save", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ customerId: customerId, signature: signature, data: toSave })
            }).catch(function (e) {
                console.warn("VORYNEX state: cloud save failed (data is still safe locally).", e);
            });
        }, 800);
    }

    function loadFromCloud() {
        const customerId = getCustomerId();
        const signature = getCustomerSignature();
        if (!customerId || !signature) return;

        fetch(VORYNEX_API_BASE + "/load?customerId=" + encodeURIComponent(customerId) + "&signature=" + encodeURIComponent(signature))
            .then(function (res) { return res.json(); })
            .then(function (cloudData) {
                if (!cloudData) return; // nothing saved in the cloud yet for this customer

                PERSISTED_SECTIONS.forEach(function (section) {
                    if (cloudData[section] !== undefined) {
                        state[section] = cloudData[section];
                    }
                });

                try {
                    const toSave = {};
                    PERSISTED_SECTIONS.forEach(function (section) { toSave[section] = state[section]; });
                    localStorage.setItem(PERSIST_KEY, JSON.stringify(toSave));
                } catch (e) {
                    console.warn("VORYNEX state: could not cache cloud data locally.", e);
                }

                // Direct call (matching how the rest of the app already
                // triggers re-renders) rather than the notify/subscribe
                // mechanism below, which nothing currently listens to.
                if (window.VORYNEX.render && window.VORYNEX.render.render) {
                    window.VORYNEX.render.render();
                }

                console.log("VORYNEX state: loaded from cloud for customer", customerId);
            })
            .catch(function (e) {
                console.warn("VORYNEX state: cloud load failed, using local data instead.", e);
            });
    }

    /*
    GDPR — "right to erasure". Deletes this customer's record from the
    Cloudflare KV store (signature-verified, same as save/load), then
    clears everything locally too (localStorage + in-memory state) so
    the person sees a genuinely fresh, empty app afterward — not just
    a cloud-side deletion while old data lingers on this device.
    Returns a Promise so the UI can show success/failure.
    */
    function deleteCloudData() {
        const customerId = getCustomerId();
        const signature = getCustomerSignature();
        if (!customerId || !signature) {
            return Promise.reject(new Error("Not signed in."));
        }

        return fetch(VORYNEX_API_BASE + "/delete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ customerId: customerId, signature: signature })
        }).then(function (res) {
            if (!res.ok) throw new Error("Delete request failed (" + res.status + ")");
            try {
                localStorage.removeItem(PERSIST_KEY);
            } catch (e) {
                console.warn("VORYNEX state: could not clear local cache.", e);
            }
            PERSISTED_SECTIONS.forEach(function (section) {
                if (section === "exerciseLogs") { state[section] = {}; }
                else if (section === "weightCheckIns") { state[section] = []; }
                else if (section === "dailyNotes") { state[section] = {}; }
                else if (section === "trainingSchedule") { state[section] = { days: [] }; }
                else if (section === "leadCapture") { state[section] = { email: null, subscribed: false, dismissed: false }; }
                else if (section === "settings") { state[section] = { units: "metric" }; }
                else if (section === "avatarConfig") { state[section] = { gender: "male", skinTone: "tone3", hairStyle: "short", hairColor: "#2b2118", outfitType: "tshirt", outfitColor: "#c9a227", accessory: "none", showBranding: false, holdingShaker: false }; }
                else if (section === "achievements") { state[section] = { unlocked: {}, firstSeenAt: null }; }
                else { state[section] = (section === "onboarding") ? { completed: false, answers: null } : null; }
            });
            return true;
        });
    }

    function savePersisted() {
        try {
            const toSave = {};
            PERSISTED_SECTIONS.forEach(function (section) {
                toSave[section] = state[section];
            });
            localStorage.setItem(PERSIST_KEY, JSON.stringify(toSave));
        } catch (e) {
            console.warn("VORYNEX state: could not persist data.", e);
        }
        scheduleCloudSave();
    }

    loadPersisted();
    loadFromCloud();

    //--------------------------------------------------
    // Subscribers
    //--------------------------------------------------

    const listeners = [];

    //--------------------------------------------------
    // Notify
    //--------------------------------------------------

    function notify() {

        listeners.forEach(function (callback) {

            callback(state);

        });

    }

    //--------------------------------------------------
    // Public API
    //--------------------------------------------------

    const State = {};

    //--------------------------------------------------
    // Get Whole State
    //--------------------------------------------------

    State.get = function () {

        return structuredClone(state);

    };

    //--------------------------------------------------
    // Get Section
    //--------------------------------------------------

    State.getSection = function (section) {

        if (!(section in state)) {

            return null;

        }

        return structuredClone(state[section]);

    };

    //--------------------------------------------------
    // Replace Section
    //--------------------------------------------------

    State.setSection = function (section, value) {

        if (!(section in state)) {

            console.warn("Unknown section:", section);

            return;

        }

        state[section] = structuredClone(value);

        if (PERSISTED_SECTIONS.indexOf(section) !== -1) {
            savePersisted();
        }

        notify();

    };

    //--------------------------------------------------
    // Update Section
    //--------------------------------------------------

    State.updateSection = function (section, values) {

        if (!(section in state)) {

            console.warn("Unknown section:", section);

            return;

        }

        Object.assign(

            state[section],

            values

        );

        notify();

    };

    //--------------------------------------------------
    // Subscribe
    //--------------------------------------------------

    State.subscribe = function (callback) {

        listeners.push(callback);

    };

    //--------------------------------------------------
    // Reset
    //--------------------------------------------------

    State.reset = function () {

        state.blueprint = null;
        state.onboarding = { completed: false, answers: null };

        try {
            localStorage.removeItem(PERSIST_KEY);
        } catch (e) {
            console.warn("VORYNEX state: could not clear persisted data.", e);
        }

        notify();

    };

    //--------------------------------------------------
    // Delete Account Data (GDPR right to erasure) —
    // removes the cloud record AND clears everything
    // local. Returns a Promise; rejects if not signed
    // in (nothing to delete cloud-side in that case).
    //--------------------------------------------------

    State.deleteAccountData = function () {
        return deleteCloudData().then(function (result) {
            notify();
            return result;
        });
    };

    //--------------------------------------------------
    // Export
    //--------------------------------------------------

    window.VORYNEX.state = State;

    console.log("State Engine Ready.");

})();